import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders, handleUnauthorized } from "../../utils/api";
import "./parent.css";

const STATUS_OPTIONS = ["", "Present", "Absent", "Late", "Leave"];

const emptyFilters = {
  status: "",
  period: "",
  day: "",
  from_date: "",
  to_date: "",
};

function ParentSubjectAttendance() {
  const { studentId, courseId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [periodOptions, setPeriodOptions] = useState([]);
  const [dayOptions, setDayOptions] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams();
        if (applied.status) params.set("status", applied.status);
        if (applied.period) params.set("period", applied.period);
        if (applied.day) params.set("day", applied.day);
        if (applied.from_date) params.set("from_date", applied.from_date);
        if (applied.to_date) params.set("to_date", applied.to_date);
        const qs = params.toString();
        const url =
          `${API_BASE_URL}/api/parents/me/children/${studentId}/subjects/${courseId}/attendance` +
          (qs ? `?${qs}` : "");
        const res = await fetch(url, { headers: getAuthHeaders() });
        if (res.status === 401) {
          handleUnauthorized();
          return;
        }
        if (!res.ok) throw new Error("Failed to load attendance");
        const payload = await res.json();
        if (!alive) return;
        setData(payload);
        // Keep dropdown options stable from API (timetable + attendance), not filtered rows
        if (Array.isArray(payload.filter_periods)) {
          setPeriodOptions(
            payload.filter_periods.map((p) => Number(p)).filter((n) => !Number.isNaN(n)),
          );
        }
        if (Array.isArray(payload.filter_days)) {
          setDayOptions(payload.filter_days.filter(Boolean));
        }
      } catch (err) {
        if (alive) {
          setData(null);
          setError(err.message || "Could not load attendance");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [studentId, courseId, applied]);

  const att = data?.attendance || {};
  const records = data?.records || [];

  const applyFilters = (e) => {
    e.preventDefault();
    setApplied({ ...filters });
  };

  const clearFilters = () => {
    setFilters(emptyFilters);
    setApplied(emptyFilters);
  };

  if (loading && !data) {
    return (
      <div className="pp-loading">
        <div className="pp-spinner" />
        <p>Loading attendance…</p>
      </div>
    );
  }

  return (
    <div className="pp-page pp-animate-in">
      <div className="pp-detail-top">
        <button
          type="button"
          className="pp-btn pp-btn--ghost pp-btn--icon"
          aria-label="Back"
          onClick={() => navigate(`/parent/children/${studentId}/subjects/${courseId}`)}
        >
          ←
        </button>
        <div>
          <p className="pp-kicker pp-kicker--dark">Attendance</p>
          <h1>{data?.course_name || "Subject attendance"}</h1>
          <p>
            {data?.student_name || "Child"}
            {data?.teacher_name ? ` · ${data.teacher_name}` : ""}
          </p>
        </div>
      </div>

      <section className="pp-stats">
        <article className="pp-stat-card pp-stat-card--teal">
          <h3>{att.attendance_rate || 0}%</h3>
          <p>Attendance rate</p>
        </article>
        <article className="pp-stat-card pp-stat-card--green">
          <h3>{att.present || 0}</h3>
          <p>Present</p>
        </article>
        <article className="pp-stat-card pp-stat-card--rose">
          <h3>{att.absent || 0}</h3>
          <p>Absent</p>
        </article>
        <article className="pp-stat-card pp-stat-card--gold">
          <h3>{att.total || 0}</h3>
          <p>Total marked</p>
        </article>
      </section>

      <form className="pp-panel pp-att-filters" onSubmit={applyFilters}>
        <label className="pp-field">
          <span>Status</span>
          <select
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s || "all"} value={s}>
                {s || "All statuses"}
              </option>
            ))}
          </select>
        </label>
        <label className="pp-field">
          <span>Period</span>
          <select
            value={filters.period}
            onChange={(e) => setFilters((f) => ({ ...f, period: e.target.value }))}
          >
            <option value="">All periods</option>
            {periodOptions.map((p) => (
              <option key={p} value={p}>
                Period {p}
              </option>
            ))}
          </select>
        </label>
        <label className="pp-field">
          <span>Day</span>
          <select
            value={filters.day}
            onChange={(e) => setFilters((f) => ({ ...f, day: e.target.value }))}
          >
            <option value="">All days</option>
            {dayOptions.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
        <label className="pp-field">
          <span>From date</span>
          <input
            type="date"
            value={filters.from_date}
            onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
          />
        </label>
        <label className="pp-field">
          <span>To date</span>
          <input
            type="date"
            value={filters.to_date}
            onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
          />
        </label>
        <div className="pp-att-filter-actions">
          <button type="submit" className="pp-btn pp-btn--primary">
            Apply filters
          </button>
          <button type="button" className="pp-btn pp-btn--ghost" onClick={clearFilters}>
            Clear
          </button>
        </div>
      </form>

      {error && <div className="pp-alert pp-alert--error">{error}</div>}

      <div className="pp-panel">
        <div className="pp-att-table-head">
          <h2>Attendance records</h2>
          <span className="pp-muted">
            {loading ? "Updating…" : `${records.length} record${records.length === 1 ? "" : "s"}`}
          </span>
        </div>
        {records.length === 0 ? (
          <div className="pp-empty">No attendance records match these filters.</div>
        ) : (
          <div className="pp-table-wrap">
            <table className="pp-table pp-att-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Day</th>
                  <th>Period</th>
                  <th>Status</th>
                  <th>Marked by</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {records.map((row) => (
                  <tr key={row.id}>
                    <td className="pp-mono">{row.date}</td>
                    <td>{row.day || "—"}</td>
                    <td>{row.period_number != null ? `Period ${row.period_number}` : "—"}</td>
                    <td>
                      <span className={`pp-status pp-status--${(row.status || "").toLowerCase()}`}>
                        {row.status}
                      </span>
                    </td>
                    <td>{row.teacher_name || "—"}</td>
                    <td>{row.remarks || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ParentSubjectAttendance;
